import React, { useState } from 'react';
import { defaultImage } from '@assets'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  Linking,
  Platform,
} from 'react-native';
import { MaterialIcons as Icon } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { getCommonStyles, isPlanLimitError, Scope, showErrorToast, showInfoToast, showSuccessToast } from '@utils';
import { useTheme } from '@react-navigation/native';
import { Theme } from '@theme';
import { Header, Reminders, SearchBar, Icon as AppIcon } from '@components';
import { CalendarEvent, useCreationStackIntegration, useGetContactById, useLoadAllPages, usePaginateCalendarEvents, usePremium, useScreen, useUnipileAccounts, useUnipileConnect, useUnipileSyncCalendar } from '@hooks';
import { usePaywall } from '@context';
import CalendarComponent from './components/CalendarComponent';
import { eventsOfDay } from './components/eventsByDate';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Dayjs } from 'dayjs';
import { useLanguage } from '@context';

export const Calendar: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute()
  const params = route.params as {contactId?: string}
  const theme = useTheme();
  const { t } = useLanguage();
  const commonStyles = getCommonStyles(theme);
  const styles = getStyles(theme);
  
  const { isWeb, isMobile, isDesktop } = useScreen();

  const { isCreationStackEnabled, showReminderCreation } = useCreationStackIntegration();

  const [searchText, setSearchText] = useState('');
  const [selectedContactId, setSelectedContactId] = useState(params?.contactId)
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null)
  // The calendar keeps its own chosen day: a new key gives a calendar with none.
  const [calendarKey, setCalendarKey] = useState(0)
  const clearSelectedDate = () => {
    setSelectedDate(null)
    setCalendarKey((key) => key + 1)
  }

  const { data: contact } = useGetContactById(selectedContactId || '');

  // No search = `undefined`, so the page shares its loaded list with the
  // other places that read every rendez-vous.
  const eventsQuery = usePaginateCalendarEvents({date: selectedDate?.format('YYYY-MM-DD'), search: searchText || undefined})
  const { flatData: events } = eventsQuery
  // The calendar dots and the list by date need every rendez-vous.
  useLoadAllPages(eventsQuery)
  // The dots do not follow the tapped day. Without a day this is the same
  // request as the list, which already loads its pages.
  const allEventsQuery = usePaginateCalendarEvents({search: searchText || undefined})
  const { flatData: allEvents } = allEventsQuery
  useLoadAllPages(allEventsQuery, !!selectedDate)
  // The list of a tapped day follows the same days as the dots.
  const listedEvents = selectedDate ? eventsOfDay(events, allEvents, selectedDate) : events

  const { data: unipileData } = useUnipileAccounts()
  const { mutate: connectUnipile, isPending: isConnectingGoogle } = useUnipileConnect()
  const { mutate: syncCalendar, isPending: isSyncingCalendar } = useUnipileSyncCalendar()
  const hasGoogleAccount = (unipileData?.accounts ?? []).some(
    (account) => account.service === 'gmail' && account.status === 'OK',
  )

  // Google Calendar sync is a Premium feature: free accounts are routed to
  // the paywall (the backend enforces it too, 402).
  const { isPremium } = usePremium()
  const { showPaywall } = usePaywall()

  const handleGoogleCalendar = () => {
    if (!isPremium) {
      showPaywall()
      return
    }
    if (!hasGoogleAccount) {
      connectUnipile('gmail', {
        onSuccess: ({ url }) => {
          if (Platform.OS === 'web') {
            window.open(url, '_blank', 'noopener')
          } else {
            Linking.openURL(url)
          }
          showInfoToast(t('connectedServices.wizardOpened'))
        },
        onError: (error) =>
          isPlanLimitError(error)
            ? showPaywall()
            : showErrorToast(t('connectedServices.wizardError')),
      })
      return
    }
    syncCalendar(undefined, {
      onSuccess: ({ reports }) => {
        const created = reports.reduce((sum, report) => sum + report.created, 0)
        const skipped = reports.reduce((sum, report) => sum + report.skippedExisting, 0)
        // A provider failure (e.g. missing calendar scopes) comes back as a
        // per-report error, not an HTTP error — surface it instead of a
        // misleading "up to date".
        if (reports.some((report) => report.error) && created === 0) {
          showErrorToast(t('connectedServices.calendarSyncError'))
          return
        }
        if (created === 0 && skipped === 0) {
          showInfoToast(t('connectedServices.calendarSyncEmpty'))
        } else {
          showSuccessToast(t('connectedServices.calendarSyncResult', { created, skipped }))
        }
      },
      onError: (error) =>
        isPlanLimitError(error)
          ? showPaywall()
          : showErrorToast(t('connectedServices.calendarSyncError')),
    })
  }

  const handleEventPress = (event: CalendarEvent) => {
    showReminderCreation({
      isCreationStack: true,
      eventId: event.id,
    })
  }

  const handleAddReminder = () => {
    if(isCreationStackEnabled) {
      showReminderCreation({
        isCreationStack: true,
        contactId: selectedContactId,
      })
    } else {
      navigation.navigate('AddEvent', { contactId: selectedContactId })
    }
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={commonStyles.flex}>
        <Header
          title={t('menuItems.appointments')}
          iconLeft={isWeb? undefined : "arrowBack"}
          iconRight={"plus"}
          onPressRight={handleAddReminder}
        />
        <ScrollView style={styles.scrollView} contentContainerStyle={isDesktop && styles.row}>
          <View>
            <View style={!isMobile? styles.searchContainerWeb : styles.searchContainerMobile}>
              <View style={styles.searchContainer}>
                <SearchBar value={searchText} onChangeText={setSearchText} hasMaxWidth={!isMobile} />
              </View>
              {/*<View style={styles.filtersContainer}>
                <ContactsFilter scope={scopes} onScopeChange={setScopes} noGhost />
              </View>*/}
            </View>
            {/* Google Calendar sync (Unipile) */}
            <TouchableOpacity
              testID="calendar-google-sync"
              style={styles.googleSyncButton}
              onPress={handleGoogleCalendar}
              disabled={isSyncingCalendar || isConnectingGoogle}
              activeOpacity={0.7}
            >
              <AppIcon name="google" size={18} color={theme.colors.primary} />
              {!isPremium && (
                <AppIcon name="star" size={16} color={theme.colors.gold} />
              )}
              <Text style={styles.googleSyncText}>
                {isSyncingCalendar
                  ? t('connectedServices.calendarSyncing')
                  : hasGoogleAccount
                    ? t('connectedServices.calendarSync')
                    : t('connectedServices.calendarConnect')}
              </Text>
              {(isSyncingCalendar || isConnectingGoogle) && (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              )}
            </TouchableOpacity>
            {/* Calendar */}
            <View style={styles.calendarContainer}>
              <CalendarComponent key={calendarKey} events={allEvents} onDayChange={setSelectedDate} allowDeselect />
            </View>
          </View>
          <View style={styles.content}>
            {contact && 
              <View
                style={styles.selectedContactItem}
              >
                <Image source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage} style={styles.selectedContactAvatar} />
                <Text style={styles.selectedContactName}>{contact.fullName}</Text>
                <TouchableOpacity style={styles.removeButton} onPress={() => setSelectedContactId('')}>
                  <Icon name="close" size={16} color={theme.colors.greyBlue}/>
                </TouchableOpacity>
              </View>
            }
            {selectedDate &&
              <View style={styles.selectedContactItem} testID="calendar-selected-day">
                <Text style={styles.selectedContactName}>{selectedDate.format('dddd D MMMM')}</Text>
                <TouchableOpacity style={styles.removeButton} onPress={clearSelectedDate} testID="calendar-clear-day">
                  <Icon name="close" size={16} color={theme.colors.greyBlue}/>
                </TouchableOpacity>
              </View>
            }
            <Reminders data={listedEvents} occurrenceDay={selectedDate} selectedContactId={selectedContactId} onEventPress={isCreationStackEnabled? handleEventPress : undefined} />
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.background,
  },
  searchContainerMobile: {
    marginBottom: theme.spacing.md,
  },
  searchContainerWeb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.lg,
  },
  searchContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
  },
  filtersContainer: {
    paddingHorizontal: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
  },
  scrollView: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
  },
  content: {
    flex: 1
  },
  googleSyncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderRadius: 12,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    marginHorizontal: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
    alignSelf: 'flex-start',
  },
  googleSyncText: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  calendarContainer: {
    backgroundColor: '#ffffff',
    margin: 20,
    borderRadius: 12,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  selectedContactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 15,
    gap: 10
  },
  selectedContactAvatar: {
    width: 25,
    height: 25,
    borderRadius: 13,
  },
  selectedContactName: {
    fontSize: 16,
    color: theme.colors.text,
  },
  removeButton: {
    padding: 4,
  },
}); 